import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { format, addHours } from "date-fns";
import { CalendarClock, Loader2, Send, Sparkles, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import PageHeader from "@/components/PageHeader";
import AiWriter, { ToneSelect } from "@/components/composer/AiWriter";
import TargetPicker from "@/components/composer/TargetPicker";
import VariantsEditor from "@/components/composer/VariantsEditor";
import ImagePicker from "@/components/composer/ImagePicker";
import AiImageGen from "@/components/composer/AiImageGen";
import { fetchBrandStyle, loadImgStyle, styleBody } from "@/components/composer/ImageStyleOptions";
import ScheduleOptions from "@/components/composer/ScheduleOptions";
import TemplateBar from "@/components/composer/TemplateBar";
import BestTimePanel from "@/components/composer/BestTimePanel";
import { ms } from "date-fns/locale";
import api, { errMsg } from "@/lib/api";

const Step = ({ n, title, children, delay = 0 }) => (
  <section className="panel fade-up p-5 sm:p-6" style={{ animationDelay: `${delay}ms` }}>
    <p className="eyebrow">Langkah {n}</p>
    <h2 className="mb-5 mt-1 text-lg font-bold">{title}</h2>
    {children}
  </section>
);

export default function Composer() {
  const navigate = useNavigate();
  const location = useLocation();
  const [groups, setGroups] = useState([]);
  const [pageConnected, setPageConnected] = useState(false);
  const [content, setContent] = useState("");
  const [imageId, setImageId] = useState(() => location.state?.imageId || null);
  const [title, setTitle] = useState(() => location.state?.imageTitle || "");
  const [imgStyle, setImgStyle] = useState(loadImgStyle);
  const [imageVary, setImageVary] = useState(false);
  const [selected, setSelected] = useState([]);
  const [includePage, setIncludePage] = useState(false);
  const [perTarget, setPerTarget] = useState(false);
  const [variants, setVariants] = useState({});
  const [when, setWhen] = useState("now");
  const [dt, setDt] = useState(format(addHours(new Date(), 1), "yyyy-MM-dd'T'HH:mm"));
  const [spacing, setSpacing] = useState(0);
  const [repeat, setRepeat] = useState("none");
  const [until, setUntil] = useState("");
  const [busy, setBusy] = useState(false);
  const [aiVary, setAiVary] = useState(() => localStorage.getItem("pp_ai_vary") === "1");
  const [aiTone, setAiTone] = useState("santai");
  const toggleAiVary = (on) => { setAiVary(on); localStorage.setItem("pp_ai_vary", on ? "1" : "0"); };

  useEffect(() => {
    api.get("/groups").then((r) => setGroups(r.data)).catch(() => {});
    api.get("/settings").then((r) => setPageConnected(r.data.page_connected)).catch(() => {});
    fetchBrandStyle().then((d) => d.saved && setImgStyle({ style: d.style, palette: d.palette, colors: d.colors?.length ? d.colors : loadImgStyle().colors })).catch(() => {});
  }, []);

  const targets = useMemo(() => {
    const list = groups.filter((g) => selected.includes(g.id)).map((g) => ({ key: g.id, kind: "group", group_id: g.id, name: g.name }));
    if (includePage) list.unshift({ key: "page", kind: "page", name: "Facebook Page" });
    return list;
  }, [groups, selected, includePage]);
  const groupCount = targets.filter((t) => t.kind === "group").length;
  const sendVary = imageVary && imageId && title.trim().length >= 2 && groupCount > 1;

  const applyTemplate = (t) => { setContent(t.content); setImageId(t.image_id || null); setVariants({}); };
  const start = when === "now" ? new Date() : new Date(dt);

  const submit = async () => {
    if (!content.trim()) return toast.error("Tulis teks post dahulu");
    if (targets.length === 0) return toast.error("Pilih sekurang-kurangnya satu sasaran");
    if (when === "later" && new Date(dt) <= new Date()) return toast.error("Pilih masa pada masa hadapan");
    setBusy(true);
    try {
      await api.post("/posts", {
        content,
        image_id: imageId,
        scheduled_at: when === "now" ? null : new Date(dt).toISOString(),
        targets: targets.map((t) => ({ kind: t.kind, group_id: t.group_id, text: (perTarget && variants[t.key]?.trim()) || content })),
        spacing_minutes: targets.length > 1 ? spacing : 0,
        repeat,
        repeat_until: repeat !== "none" && until ? new Date(`${until}T23:59:59`).toISOString() : null,
        ai_vary: aiVary && targets.some((t) => t.kind === "group"),
        ai_tone: aiTone,
        image_vary: sendVary ? { title: title.trim(), ...styleBody(imgStyle) } : null,
      });
      toast.success(when === "now" && !spacing ? "Post dimasukkan ke giliran sekarang" : "Post dijadualkan");
      if (sendVary) toast.info(`AI sedang cipta ${groupCount - 1} gambar berbeza di latar. Group guna gambar utama sehingga versinya siap.`, { duration: 8000 });
      navigate("/jadual");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader eyebrow="Tulis Post" title="Post baharu" desc="Tulis sekali, pilih group & Page, biar AI olah ayat supaya setiap group dapat teks berbeza." />
      <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
        <div className="space-y-6">
          <Step n={1} title="Teks post">
            <AiImageGen title={title} setTitle={setTitle} imageId={imageId} setImageId={setImageId} imgStyle={imgStyle} setImgStyle={setImgStyle} vary={imageVary} setVary={setImageVary} groupCount={groupCount} />
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <AiWriter onWrite={setContent} />
              <TemplateBar content={content} imageId={imageId} onApply={applyTemplate} initialId={location.state?.templateId} />
            </div>
            <Textarea value={content} onChange={(e) => setContent(e.target.value)} rows={8} placeholder="Tulis post anda di sini..." className="bg-background text-[15px] leading-relaxed" data-testid="composer-content-textarea" />
            <div className="mt-4 flex items-center justify-between">
              <ImagePicker imageId={imageId} setImageId={setImageId} />
              <span className="font-mono text-xs text-muted-foreground">{content.length} aksara</span>
            </div>
          </Step>
          <Step n={3} title="Teks untuk setiap sasaran" delay={120}>
            <div className="mb-4 rounded-lg border border-violet-500/30 bg-violet-500/5 p-3" data-testid="composer-ai-auto-vary-row">
              <label className="flex items-center justify-between gap-3">
                <div>
                  <p className="flex items-center gap-1.5 text-sm font-medium"><Sparkles className="h-3.5 w-3.5 text-violet-300" /> AI tulis versi berbeza untuk setiap group</p>
                  <p className="text-xs text-muted-foreground">Automatik semasa simpan - setiap group dapat teks unik (kurangkan risiko disekat spam)</p>
                </div>
                <Switch checked={aiVary} onCheckedChange={toggleAiVary} data-testid="composer-ai-auto-vary-toggle" />
              </label>
              {aiVary && <div className="mt-3"><ToneSelect value={aiTone} onChange={setAiTone} testId="composer-ai-auto-vary-tone" /></div>}
            </div>
            <label className="mb-4 flex items-center justify-between rounded-lg border border-border p-3">
              <div>
                <p className="text-sm font-medium">Teks berbeza untuk setiap group</p>
                <p className="text-xs text-muted-foreground">Elak Facebook tandakan post anda sebagai spam</p>
              </div>
              <Switch checked={perTarget} onCheckedChange={setPerTarget} data-testid="composer-per-target-toggle" />
            </label>
            {perTarget && <VariantsEditor content={content} targets={targets} variants={variants} setVariants={setVariants} />}
          </Step>
        </div>
        <div className="space-y-6">
          <Step n={2} title="Sasaran" delay={60}>
            <TargetPicker groups={groups} selected={selected} setSelected={setSelected} includePage={includePage} setIncludePage={setIncludePage} pageConnected={pageConnected} />
          </Step>
          <Step n={4} title="Masa" delay={180}>
            <div className="grid grid-cols-2 gap-2">
              {[{ v: "now", l: "Sekarang", i: Zap }, { v: "later", l: "Jadualkan", i: CalendarClock }].map(({ v, l, i: Icon }) => (
                <button key={v} type="button" onClick={() => setWhen(v)} className={`flex items-center justify-center gap-2 rounded-lg border py-2.5 text-sm transition-colors ${when === v ? "border-primary bg-primary/15 text-white" : "border-border text-muted-foreground hover:text-white"}`} data-testid={`composer-when-${v}`}>
                  <Icon className="h-4 w-4" /> {l}
                </button>
              ))}
            </div>
            {when === "later" && <Input type="datetime-local" value={dt} onChange={(e) => setDt(e.target.value)} className="mt-3 bg-background" data-testid="composer-datetime-input" />}
            <BestTimePanel groupIds={selected} onPick={(d) => { setWhen("later"); setDt(format(d, "yyyy-MM-dd'T'HH:mm")); toast.success(`Masa ditetapkan: ${format(d, "EEEE, h:mm a", { locale: ms })}`); }} />
            <ScheduleOptions start={isNaN(start) ? new Date() : start} count={targets.length} spacing={spacing} setSpacing={setSpacing} repeat={repeat} setRepeat={setRepeat} until={until} setUntil={setUntil} />
            <Button onClick={submit} disabled={busy} className="mt-5 h-11 w-full font-semibold" data-testid="composer-submit-btn">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
              {busy && aiVary ? "AI sedang tulis versi unik..." : when === "now" ? "Post sekarang" : "Jadualkan post"}
            </Button>
          </Step>
        </div>
      </div>
    </div>
  );
}
